<template>
  <div class="privacy-page">
    <header class="site-header">
      <a class="brand" href="/" aria-label="Home">
        <span class="brand-mark">C</span>
        <span class="brand-text">
          <strong>{{ platform_name }}</strong>
          <small>Privacy and data protection</small>
        </span>
      </a>
      <a class="header-link" href="#contact">Contact us</a>
    </header>

    <main>
      <section class="hero">
        <div class="hero-content">
          <div class="eyebrow"><span class="status-dot"></span> YOUR PRIVACY MATTERS</div>
          <h1>Privacy <span>Policy</span></h1>
          <p class="hero-description">
            Learn how {{ church_name }} and {{ app_display_name }} use, store, and protect your
            personal information when you use our digital services.
          </p>
          <div class="policy-meta">
            <span><span class="meta-label">Effective date</span><strong>{{ effectiveDate }}</strong></span>
            <span class="meta-divider"></span>
            <span><span class="meta-label">Applies to</span><strong>{{ app_display_name }}</strong></span>
            <span class="meta-divider"></span>
            <span><span class="meta-label">Last updated</span><strong>{{ lastUpdated }}</strong></span>
          </div>
        </div>
        <div class="hero-decoration" aria-hidden="true">
          <div class="decoration-ring ring-one"></div><div class="decoration-ring ring-two"></div>
          <div class="decoration-center">
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M32 7L52 15V29C52 42 43.5 52 32 57C20.5 52 12 42 12 29V15L32 7Z" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round" />
              <path d="M23 32L29 38L42 25" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </div>
      </section>

      <section class="content-layout">
        <aside class="sidebar">
          <div class="sidebar-inner">
            <p class="sidebar-heading">ON THIS PAGE</p>
            <nav aria-label="Privacy policy sections">
              <a v-for="section in sections" :key="section.id" :href="`#${section.id}`">{{ section.label }}</a>
            </nav>
            <div class="sidebar-note"><span class="note-icon">i</span><p>We believe you should understand how your information is handled.</p></div>
          </div>
        </aside>

        <article class="policy-content">
          <div class="intro-card">
            <span class="intro-icon">✓</span>
            <div><h2>Your information, treated with care</h2><p>This policy explains what information we collect, why we use it, and the choices and rights available to you.</p></div>
          </div>

          <section id="introduction" class="policy-section">
            <span class="section-number">01</span><h2>Introduction</h2>
            <p>Welcome to {{ app_display_name }}, the application used by {{ church_name }}.</p>
            <p>We respect your privacy and are committed to handling your personal information responsibly. This Privacy Policy explains what information we collect, how we use and protect it, who may access it, and the choices available to you.</p>
            <p>In this policy, “we,” “us,” and “our” refer to {{ church_name }} when discussing membership, attendance, and church activities. The platform operator provides the technology and may have separate responsibilities depending on the processing activity and applicable law.</p>
          </section>

          <section id="responsibility" class="policy-section">
            <span class="section-number">02</span><h2>Who is responsible for your information?</h2>
            <p>{{ church_name }} determines how membership and church-related information is used for its activities, subject to applicable data-protection laws.</p>
            <p>{{ platform_operator_name }} provides and maintains the technology that supports the app. Depending on the processing activity, the platform operator may process information on behalf of the church or have separate responsibilities for its own platform operations.</p>
            <p>The platform operator has stated that its staff do not have access to the underlying member database. Appropriate technical controls should be maintained to support this restriction.</p>
            <h3>Church privacy contact</h3>
            <p>For questions about membership, attendance, or other church-related personal information, contact {{ church_name }} at <a :href="`mailto:${church_privacy_email}`">{{ church_privacy_email }}</a>.</p>
            <p v-if="church_website">Website: <a :href="church_website" target="_blank" rel="noopener noreferrer">{{ church_website }}</a></p>
            <h3>Platform contact</h3>
            <p>For platform-related enquiries, contact {{ platform_operator_name }}<template v-if="platform_privacy_email"> at <a :href="`mailto:${platform_privacy_email}`">{{ platform_privacy_email }}</a></template><template v-if="platform_website"> or visit <a :href="platform_website" target="_blank" rel="noopener noreferrer">{{ platform_website }}</a></template>.</p>
          </section>

          <section id="information" class="policy-section">
            <span class="section-number">03</span><h2>Personal information we collect</h2>
            <h3>Account and membership information</h3>
            <p>When you register, we collect the information required to create and manage your account, including:</p>
            <ul><li>Full name and gender</li><li>Date of birth and marital status</li><li>Phone number and email address</li><li>Password and related authentication information</li></ul>
            <p>Passwords are handled using password-storage safeguards and are not intended to be stored as plain text.</p>
            <h3>Attendance and group information</h3>
            <p>We may process your membership or account reference, the church activity or attendance event, the date and time, attendance status, and your association with a core group or other church group.</p>
            <h3>Device and technical information</h3>
            <p>Technical information may be processed as necessary to connect to the service, authenticate your account, respond to requests, and maintain security. We do not currently use analytics or crash-reporting tools as part of the app setup described in this policy.</p>
          </section>

          <section id="usage" class="policy-section">
            <span class="section-number">04</span><h2>How we use your information</h2>
            <ul><li>Create and manage your account.</li><li>Maintain church membership records.</li><li>Authenticate users and protect accounts.</li><li>Record and manage attendance.</li><li>Organize church groups and membership associations.</li><li>Send relevant church announcements and notifications.</li><li>Help designated core group members follow up with absent members.</li><li>Respond to privacy requests and account enquiries.</li><li>Maintain the security and proper functioning of the app.</li></ul>
            <p>We aim to use information only for purposes relevant to providing the app, supporting church activities, or other purposes permitted by applicable law.</p>
          </section>

          <section id="attendance" class="policy-section">
            <span class="section-number">05</span><h2>Attendance and notifications</h2>
            <p>The app uses the device camera to scan barcodes for attendance purposes. The camera image is processed to read the barcode and is not intended to be retained as an image. Information obtained from the barcode may be used to update the church's attendance records.</p>
            <p>Authorized administrators of {{ church_name }} can access attendance records for their church. When a member is recorded as absent, their designated core group may receive a notification encouraging its members to check on them. This does not give core group members general access to the member's full attendance history.</p>
            <p>Attendance-related notifications are intended to expire and be deleted after 24 hours. This period applies to the temporary notification, not necessarily to the underlying attendance record retained for church administration.</p>
            <p>Notifications may appear when the app is not open. You can manage notification permissions through your Android device settings.</p>
          </section>

          <section id="sharing" class="policy-section">
            <span class="section-number">06</span><h2>When we share information</h2>
            <p>We do not sell your personal information. Information may be accessed or disclosed in these limited circumstances:</p>
            <ul><li><strong>Authorized church administrators:</strong> may access information needed for their assigned responsibilities and belonging to their own church.</li><li><strong>Designated core group members:</strong> may receive relevant attendance follow-up notifications.</li><li><strong>Platform operations and providers:</strong> the platform operator maintains the technology. If service providers process personal information for hosting, notification delivery, or another function, appropriate safeguards should apply.</li><li><strong>Legal requirements:</strong> information may be disclosed where required by law or a valid legal process, or where otherwise permitted by law to protect rights, safety, or security.</li></ul>
          </section>

          <section id="permissions" class="policy-section">
            <span class="section-number">07</span><h2>Device permissions</h2>
            <p>Some features require permission from your device. You can manage available permissions through Android settings. Disabling a permission may prevent the related feature from working.</p>
            <div class="permission-list">
              <div class="permission-item"><span class="permission-symbol">▦</span><div><h3>Camera</h3><p>Scans barcodes for attendance. The camera image is not intended to be retained.</p></div></div>
              <div class="permission-item"><span class="permission-symbol">↗</span><div><h3>Phone and calling</h3><p>Allows you to initiate a direct call when you select a phone number in the app.</p></div></div>
              <div class="permission-item"><span class="permission-symbol">▤</span><div><h3>Files and documents</h3><p>Supports document-related features when access to a file on your device is required.</p></div></div>
              <div class="permission-item"><span class="permission-symbol">♧</span><div><h3>Notifications</h3><p>Supports relevant announcements, attendance follow-up, and other app updates.</p></div></div>
              <div class="permission-item"><span class="permission-symbol">♫</span><div><h3>Background audio playback</h3><p>A foreground service may be used to keep sermon recordings and other audio playing when 
                you switch to another app or lock your device. This supports uninterrupted playback and may display a playback notification while audio is active.
                </p>
              </div>
            </div>
            </div>
          </section>

          <section id="security" class="policy-section">
            <span class="section-number">08</span><h2>Security</h2>
            <p>Information transmitted between the app and the server is protected using an encrypted connection. We use access restrictions intended to protect personal information against unauthorized access, alteration, disclosure, loss, or destruction.</p>
            <p>No method of electronic transmission or storage can be guaranteed to be completely secure. We cannot promise absolute security.</p>
            <p>We do not currently maintain backups as part of the setup described for this app. Information may therefore be unrecoverable if it is deleted or lost and is not otherwise retained. We do not promise backup-based recovery.</p>
          </section>

          <section id="retention" class="policy-section">
            <span class="section-number">09</span><h2>Data retention</h2>
            <p>We retain personal information for as long as reasonably necessary for the purposes described in this policy, subject to applicable legal requirements.</p>
            <ul><li><strong>Account information:</strong> retained while needed to operate the account, subject to closure, deletion requests, and applicable requirements.</li><li><strong>Attendance records:</strong> may be retained for legitimate church recordkeeping purposes, subject to applicable law and the anonymization approach described below.</li><li><strong>Temporary attendance notifications:</strong> intended to expire and be deleted after 24 hours.</li><li><strong>Information retained for legal reasons:</strong> may be retained for the period required by the relevant obligation or permitted legal basis.</li></ul>
            <p>When information is no longer needed, it should be securely deleted or irreversibly anonymized, as appropriate.</p>
          </section>

          <section id="transfers" class="policy-section">
            <span class="section-number">10</span><h2>International processing and data transfers</h2>
            <p>Your personal information may be processed or stored outside Nigeria, including through global cloud service providers and other infrastructure used to operate the app.</p>
            <p>Where personal information is transferred outside Nigeria, we will take appropriate steps to ensure that the transfer is lawful and that your information is protected in accordance with applicable data-protection laws.</p>
            <p>The location of servers and service providers may vary depending on the platform's infrastructure. We will take reasonable steps to ensure that providers processing personal information are subject to appropriate privacy and security safeguards.</p>
          </section>

          <section id="rights" class="policy-section">
            <span class="section-number">11</span><h2>Your privacy rights</h2>
            <p>Subject to applicable law, you may have the right to:</p>
            <ul><li>Request access to your personal information.</li><li>Request correction of inaccurate information.</li><li>Request deletion of your account and associated personal information.</li><li>Object to or request restrictions on certain processing.</li><li>Withdraw consent where processing is based on consent.</li><li>Raise a complaint with the appropriate data-protection authority.</li></ul>
            <p>To exercise your rights concerning church membership or attendance information, contact {{ church_privacy_email }}. We may need to verify your identity before responding. Requests will be handled in accordance with applicable law.</p>
          </section>

          <section id="accounts" class="policy-section">
            <span class="section-number">12</span><h2>Account closure and deletion</h2>
            <div class="important-card"><span class="important-icon">!</span><div><h3>Closing an account is different from deleting it</h3><p>Account closure is reversible for a limited period. A deletion request is a separate process and must not be replaced by simple deactivation.</p></div></div>
            <h3>Temporary account closure</h3>
            <p>If you choose to close your account temporarily, it is intended to become unavailable for normal use and you will be logged out. You may request restoration within 30 days of closure by verifying the email address associated with your account.</p>
            <p>Temporary closure does not constitute a permanent deletion request. The account and information retained for recovery are subject to applicable law and the actual account-closure process.</p>
            <h3>Permanent account deletion</h3>
            <p>You may request permanent account deletion through the in-app deletion option or the designated external account-deletion webpage. You do not have to close your account and wait 30 days before requesting deletion.</p>
            <p>When a deletion request is processed, the account and associated personal information will be deleted, except information subject to a specific lawful retention requirement or other applicable legal basis. Any retained information should be limited to what is necessary for that purpose.</p>
            <p>Necessary attendance records may be retained where justified and lawful, with identifying information irreversibly anonymized so the records can no longer reasonably be linked to you. Removing only your name may not be sufficient if other identifiers remain.</p>
            <a style="display: none;" class="action-link" :href="deletionUrl">Go to account deletion <span aria-hidden="true">↗</span></a>
          </section>

          <section id="age" class="policy-section">
            <span class="section-number">13</span><h2>Minimum age</h2>
            <p>You must be at least 18 years old to register for or use {{ app_display_name }}. By registering, you confirm that you meet this minimum age requirement.</p>
            <p>If we become aware that an account belongs to someone under 18, appropriate steps will be taken to restrict or terminate the account and handle the associated personal information in accordance with applicable law.</p>
            <p>If you believe someone under 18 has created an account, contact {{ church_privacy_email }}.</p>
          </section>

          <section id="third-parties" class="policy-section">
            <span class="section-number">14</span><h2>Third-party services</h2>
            <p>The current setup described for the app does not include analytics or crash-reporting tools. The hosting arrangements and push-notification delivery method still need to be confirmed.</p>
            <p>If external providers process personal information to host the service, deliver notifications, or support another feature, this policy should be updated to describe the relevant processing accurately. External providers may have their own terms and privacy policies.</p>
          </section>

          <section id="changes" class="policy-section">
            <span class="section-number">15</span><h2>Changes to this policy</h2>
            <p>We may update this Privacy Policy when the app's features, data practices, legal requirements, or operational arrangements change.</p>
            <p>The updated policy will be published at the designated privacy-policy address for {{ app_display_name }}. The last-updated date will be revised when changes are made. Where applicable law requires notice or consent for a material change, appropriate steps will be taken.</p>
          </section>

          <section id="contact" class="contact-card">
            <div class="contact-icon"><svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.7"/><path d="M4 7L12 13L20 7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
            <p class="contact-eyebrow">HAVE A QUESTION?</p><h2>Contact us about your privacy</h2>
            <p>If you have questions about this policy, your personal information, or an account deletion request, contact {{ church_name }}.</p>
            <a class="contact-email" :href="`mailto:${church_privacy_email}`">{{ church_privacy_email }}</a>
            <p class="contact-address">{{ church_name }}<span v-if="church_website"> · <a :href="church_website" target="_blank" rel="noopener noreferrer">{{ church_website }}</a></span></p>
            <p v-if="platform_privacy_email" class="contact-address">Platform support: <a :href="`mailto:${platform_privacy_email}`">{{ platform_privacy_email }}</a></p>
          </section>
          <footer class="policy-footer"><span>© {{ currentYear }} {{ church_name }}</span><span>Privacy matters. Always.</span></footer>
        </article>
      </section>
    </main>
  </div>
</template>

<script setup>
// Replace these values with server-provided configuration for each church.
// For a multi-tenant deployment, prefer props or data returned by your API over hardcoded values.
const church_name = "Springhill Ministries"
const app_display_name = "Springhill Connect"
const platform_name = "ChurchinPhone"
const platform_operator_name = "ChurchInPhone LTD"
const church_privacy_email = "privacy@shmng.org"
const church_website = "shmng.org"
const platform_privacy_email = "privacy@churchinphone.com"
const platform_website = "churchinphone.com"
const effectiveDate = "23 October 2026"
const lastUpdated = "23 October 2026"
const deletionUrl = "/deletion"
const currentYear = new Date().getFullYear()

const sections = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'responsibility', label: 'Who is responsible?' },
  { id: 'information', label: 'Information collected' },
  { id: 'usage', label: 'How we use information' },
  { id: 'attendance', label: 'Attendance and notifications' },
  { id: 'sharing', label: 'Information sharing' },
  { id: 'permissions', label: 'Device permissions' },
  { id: 'security', label: 'Security and church separation' },
  { id: 'retention', label: 'Data retention' },
  { id: 'transfers', label: 'International transfers' },
  { id: 'rights', label: 'Your privacy rights' },
  { id: 'accounts', label: 'Account closure and deletion' },
  { id: 'age', label: 'Minimum age' },
  { id: 'third-parties', label: 'Third-party services' },
  { id: 'changes', label: 'Policy changes' },
  { id: 'contact', label: 'Contact us' }
]
</script>

<style>
:root {
  --privacy-primary: color(srgb 0.1083 0.1703 0.308);
  --privacy-dark: color(srgb 0.0485 0.0793 0.1295);
  --privacy-white: #ffffff;
  --privacy-muted: #667085;
  --privacy-border: #e5e9f0;
  --privacy-background: #f7f9fc;
  --privacy-accent: #a8c5ff;
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  scroll-padding-top: 32px;
}

body {
  margin: 0;
  font-family:
    Inter, -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  color: var(--privacy-dark);
  background: var(--privacy-white);
  line-height: 1.7;
  -webkit-font-smoothing: antialiased;
}

a {
  color: inherit;
}

.privacy-page {
  min-height: 100vh;
  overflow: clip;
}

.site-header {
  height: 82px;
  padding: 0 7%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--privacy-white);
  border-bottom: 1px solid var(--privacy-border);
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.brand-mark {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  color: white;
  background: var(--privacy-primary);
  border-radius: 12px;
  font-size: 20px;
  font-weight: 800;
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.4;
}

.brand-text strong {
  font-size: 15px;
  letter-spacing: -0.3px;
}

.brand-text small {
  color: var(--privacy-muted);
  font-size: 11px;
}

.header-link {
  padding: 9px 18px;
  color: var(--privacy-primary);
  border: 1px solid var(--privacy-border);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s;
}

.header-link:hover {
  background: var(--privacy-background);
  border-color: #c6d0df;
}

.hero {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 355px;
  padding: 64px 10%;
  overflow: hidden;
  color: white;
  background: var(--privacy-dark);
}

.hero-content {
  position: relative;
  z-index: 1;
  max-width: 650px;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 22px;
  color: var(--privacy-accent);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 2px;
}

.status-dot {
  width: 7px;
  height: 7px;
  background: #a8c5ff;
  border-radius: 50%;
}

.hero h1 {
  margin: 0;
  font-size: clamp(42px, 5vw, 64px);
  font-weight: 750;
  line-height: 1.1;
  letter-spacing: -2.5px;
}

.hero h1 span {
  color: var(--privacy-accent);
}

.hero-description {
  max-width: 570px;
  margin: 22px 0 30px;
  color: #c2ccdc;
  font-size: 15px;
  line-height: 1.9;
}

.policy-meta {
  display: flex;
  align-items: center;
  gap: 25px;
}

.policy-meta > span:not(.meta-divider) {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.meta-label {
  color: #9eacc2;
  font-size: 11px;
}

.policy-meta strong {
  color: #f5f7fb;
  font-size: 12px;
  font-weight: 600;
}

.meta-divider {
  height: 33px;
  width: 1px;
  background: #39445a;
}

.hero-decoration {
  position: absolute;
  right: 7%;
  top: 50%;
  width: 280px;
  height: 280px;
  transform: translateY(-50%);
  opacity: 0.8;
}

.decoration-ring {
  position: absolute;
  inset: 0;
  border: 1px solid #536581;
  border-radius: 50%;
}

.ring-one {
  transform: scale(0.78);
}

.ring-two {
  transform: scale(0.53);
  border-color: #8296b5;
}

.decoration-center {
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  place-items: center;
  width: 90px;
  height: 90px;
  color: #b7ccf4;
  background: #1d2b44;
  border: 1px solid #4b6082;
  border-radius: 25px;
  transform: translate(-50%, -50%) rotate(-8deg);
}

.decoration-center svg {
  width: 48px;
  height: 48px;
}

.content-layout {
  display: grid;
  grid-template-columns: 235px minmax(0, 760px);
  justify-content: center;
  gap: 70px;
  padding: 60px 6% 80px;
  align-items: start;
}

.sidebar {
  align-self: stretch;
}

.sidebar-inner {
  position: sticky;
  top: 30px;
}

.sidebar-heading {
  margin: 0 0 20px;
  color: #8993a4;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.6px;
}

.sidebar nav {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-left: 12px;
  border-left: 1px solid var(--privacy-border);
}

.sidebar nav a {
  padding: 7px 0 7px 13px;
  margin-left: -13px;
  color: #657084;
  border-left: 2px solid transparent;
  font-size: 12px;
  line-height: 1.5;
  text-decoration: none;
  transition: color 0.2s, border-color 0.2s;
}

.sidebar nav a:hover,
.sidebar nav a:focus-visible {
  color: var(--privacy-primary);
  border-left-color: var(--privacy-primary);
}

.sidebar-note {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-top: 28px;
  padding: 16px;
  background: var(--privacy-background);
  border: 1px solid var(--privacy-border);
  border-radius: 10px;
}

.note-icon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 20px;
  height: 20px;
  color: white;
  background: var(--privacy-primary);
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
}

.sidebar-note p {
  margin: 0;
  color: #667085;
  font-size: 11px;
  line-height: 1.7;
}

.policy-content {
  min-width: 0;
}

.intro-card {
  display: flex;
  gap: 16px;
  padding: 22px;
  margin-bottom: 48px;
  background: #f6f8fc;
  border: 1px solid #e7ecf5;
  border-radius: 12px;
}

.intro-icon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 34px;
  height: 34px;
  color: white;
  background: var(--privacy-primary);
  border-radius: 10px;
  font-weight: 700;
}

.intro-card h2 {
  margin: 0 0 5px;
  color: var(--privacy-primary);
  font-size: 15px;
  letter-spacing: -0.3px;
}

.intro-card p {
  margin: 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.8;
}

.policy-section {
  position: relative;
  padding: 0 0 36px;
  margin-bottom: 38px;
  border-bottom: 1px solid var(--privacy-border);
  scroll-margin-top: 28px;
}

.section-number {
  display: inline-block;
  margin-bottom: 7px;
  color: #7189b4;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
}

.policy-section h2,
.contact-card h2 {
  margin: 0 0 17px;
  color: var(--privacy-primary);
  font-size: 25px;
  line-height: 1.35;
  letter-spacing: -0.7px;
}

.policy-section h3 {
  margin: 25px 0 8px;
  color: var(--privacy-primary);
  font-size: 15px;
  font-weight: 700;
}

.policy-section p {
  margin: 0 0 15px;
  color: #505c70;
  font-size: 13px;
  line-height: 1.95;
}

.policy-section ul {
  display: grid;
  gap: 10px;
  margin: 14px 0 20px;
  padding-left: 21px;
  color: #505c70;
  font-size: 13px;
  line-height: 1.9;
}

.policy-section li {
  padding-left: 3px;
}

.policy-section li::marker {
  color: #748cb5;
}

.permission-list {
  display: grid;
  gap: 12px;
  margin-top: 20px;
}

.permission-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 17px;
  border: 1px solid var(--privacy-border);
  border-radius: 10px;
  background: white;
}

.permission-symbol {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 38px;
  height: 38px;
  color: var(--privacy-primary);
  background: #edf2fb;
  border-radius: 10px;
  font-size: 18px;
}

.permission-item h3 {
  margin: 0 0 3px;
  font-size: 13px;
}

.permission-item p {
  margin: 0;
  font-size: 12px;
  line-height: 1.8;
}

.important-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px;
  margin: 22px 0;
  background: #f2f5fb;
  border: 1px solid #dce4f1;
  border-radius: 10px;
}

.important-icon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 27px;
  height: 27px;
  color: white;
  background: var(--privacy-primary);
  border-radius: 50%;
  font-size: 15px;
  font-weight: 800;
}

.important-card h3 {
  margin: 0 0 7px;
  font-size: 14px;
}

.important-card p {
  margin: 0;
  font-size: 12px;
}

.action-link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 17px;
  margin-top: 5px;
  color: white;
  background: var(--privacy-primary);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 650;
  text-decoration: none;
  transition: background 0.2s, transform 0.2s;
}

.action-link:hover {
  background: var(--privacy-dark);
  transform: translateY(-1px);
}

.contact-card {
  padding: 32px;
  margin-top: 50px;
  color: white;
  background: var(--privacy-dark);
  border-radius: 14px;
  scroll-margin-top: 28px;
}

.contact-icon {
  display: grid;
  place-items: center;
  width: 43px;
  height: 43px;
  margin-bottom: 23px;
  color: #c5d7fa;
  background: #1c2a42;
  border: 1px solid #384965;
  border-radius: 11px;
}

.contact-icon svg {
  width: 23px;
  height: 23px;
}

.contact-eyebrow {
  margin: 0 0 9px;
  color: #a8c5ff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.6px;
}

.contact-card h2 {
  margin-bottom: 12px;
  color: white;
  font-size: 24px;
}

.contact-card > p:not(.contact-eyebrow):not(.contact-address) {
  margin: 0 0 20px;
  color: #b9c5d8;
  font-size: 13px;
  line-height: 1.9;
}

.contact-email {
  display: inline-block;
  color: #c5d7fa;
  font-size: 14px;
  font-weight: 650;
  text-decoration: none;
  overflow-wrap: anywhere;
}

.contact-email:hover {
  text-decoration: underline;
}

.contact-address {
  margin: 22px 0 0;
  padding-top: 18px;
  color: #9eacc2;
  border-top: 1px solid #354159;
  font-size: 12px;
}

.policy-footer {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  padding-top: 25px;
  color: #8791a2;
  font-size: 11px;
}

@media (min-width: 1500px) {
  .content-layout {
    grid-template-columns: 245px minmax(0, 800px);
    gap: 85px;
  }
}

@media (max-width: 1050px) {
  .hero {
    padding-left: 7%;
    padding-right: 7%;
  }

  .hero-decoration {
    right: -30px;
    opacity: 0.35;
  }

  .content-layout {
    grid-template-columns: 190px minmax(0, 760px);
    gap: 35px;
    padding-left: 5%;
    padding-right: 5%;
  }
}

@media (max-width: 720px) {
  .site-header {
    height: 72px;
    padding: 0 5%;
  }

  .brand-mark {
    width: 38px;
    height: 38px;
  }

  .brand-text strong {
    font-size: 13px;
  }

  .brand-text small {
    font-size: 10px;
  }

  .header-link {
    padding: 8px 12px;
    font-size: 12px;
  }

  .hero {
    min-height: auto;
    padding: 55px 6% 48px;
  }

  .hero h1 {
    font-size: 46px;
    letter-spacing: -1.8px;
  }

  .hero-description {
    font-size: 14px;
  }

  .hero-decoration {
    top: auto;
    right: -95px;
    bottom: -95px;
    width: 230px;
    height: 230px;
    transform: none;
    opacity: 0.16;
  }

  .policy-meta {
    gap: 16px;
    flex-wrap: wrap;
  }

  .content-layout {
    display: block;
    padding: 35px 6% 55px;
  }

  .sidebar {
    margin-bottom: 35px;
  }

  .sidebar-inner {
    position: static;
  }

  .sidebar-heading {
    margin-bottom: 12px;
  }

  .sidebar nav {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px 14px;
    padding-left: 0;
    border: 0;
  }

  .sidebar nav a {
    margin-left: 0;
    padding: 7px 9px;
    border-left: 0;
    border-radius: 5px;
    background: var(--privacy-background);
    font-size: 11px;
  }

  .sidebar nav a:hover,
  .sidebar nav a:focus-visible {
    background: #edf2fb;
  }

  .sidebar-note {
    margin-top: 14px;
  }

  .intro-card {
    padding: 17px;
    margin-bottom: 38px;
  }

  .policy-section h2 {
    font-size: 23px;
  }

  .policy-section {
    padding-bottom: 28px;
    margin-bottom: 30px;
  }

  .contact-card {
    padding: 25px 21px;
    margin-top: 35px;
  }

  .policy-footer {
    flex-direction: column;
    gap: 5px;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    transition-duration: 0.01ms !important;
  }
}
</style>
